import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import { Send, Check, Clock, ArrowRight, ArrowLeft, MessageCircle, Loader2, XCircle, Eye, Users, Settings2, AlertTriangle, Play, Shield, Wifi } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button, Card, Badge } from "@/components/kit";
import UpgradeGate from "@/components/UpgradeGate";
import BlastActivityFeed from "@/components/BlastActivityFeed";
import LeadPicker from "@/components/LeadPicker";
import DeviceDisconnectWarning from "@/components/DeviceDisconnectWarning";

const SAFE_MIN_DELAY = 15; // seconds — mirrors backend SAFE_DEFAULTS.min_delay

export default function Blast() {
  const { quota, refreshQuota } = useAuth();
  const [step, setStep] = useState(1);
  const [leads, setLeads] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [waConnected, setWaConnected] = useState(null);
  const [waDevice, setWaDevice] = useState({});
  const [selected, setSelected] = useState({});
  const [templateId, setTemplateId] = useState("");
  const [deviceId, setDeviceId] = useState("auto");
  const [delay, setDelay] = useState({ min: 5, max: 30 });
  const [schedule, setSchedule] = useState("");
  const [useSchedule, setUseSchedule] = useState(false);
  const [ignoreCooldown, setIgnoreCooldown] = useState(false);
  const [job, setJob] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadWa = () => api.get("/wa/account")
    .then((r) => { setWaConnected(!!r.data.connected); setWaDevice(r.data.device || {}); })
    .catch(() => setWaConnected(false));

  useEffect(() => {
    api.get("/leads", { params: { per_page: 1000, has_phone: true } }).then((r) => setLeads(r.data.leads)).catch(() => {});
    api.get("/templates").then((r) => setTemplates(r.data.templates.filter((t) => (t.type || "wa") === "wa"))).catch(() => {});
    loadWa();
  }, []);

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const selectedLeads = leads.filter((l) => selected[l.id]);
  const selectedTemplate = templates.find((t) => t.id === templateId);

  const wsRef = useRef(null);

  const connectWs = useCallback((jobId) => {
    const base = (api.defaults.baseURL || "").replace(/\/api$/, "").replace(/^http/, "ws");
    const token = localStorage.getItem("__session") || "";
    const ws = new WebSocket(`${base}/ws/blast/${jobId}?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "progress") {
          setJob({
            status: msg.status, sent: msg.sent, failed: msg.failed,
            skipped: msg.skipped, progress_pct: msg.progress_pct, total_leads: msg.total,
          });
          if (["done", "cancelled", "failed", "paused"].includes(msg.status)) {
            ws.close(); refreshQuota();
          }
        } else if (msg.type === "complete") {
          ws.close(); refreshQuota();
        }
      } catch {}
    };
    ws.onerror = () => { ws.close(); pollFallback(jobId); };
    ws.onclose = () => { wsRef.current = null; };
  }, [refreshQuota]);

  // Fallback to polling if WebSocket fails
  const pollRef = useRef(null);
  const pollFallback = useCallback((jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/blast/wa/${jobId}`);
        setJob(data);
        if (["done", "cancelled", "failed", "paused"].includes(data.status)) {
          clearInterval(pollRef.current); refreshQuota();
        }
      } catch { clearInterval(pollRef.current); }
    }, 2500);
  }, [refreshQuota]);

  useEffect(() => () => { clearInterval(pollRef.current); if (wsRef.current) wsRef.current.close(); }, []);

  const startBlast = async () => {
    if (useSchedule && !schedule) { toast.error("Pilih tanggal & jam dulu"); return; }
    const scheduled_at = useSchedule && schedule ? new Date(schedule).toISOString() : null;
    setLoading(true);
    try {
      const { data } = await api.post("/blast/wa", {
        lead_ids: selectedIds, template_id: templateId, device_id: deviceId,
        delay_min: delay.min, delay_max: delay.max, scheduled_at, ignore_cooldown: ignoreCooldown,
      });
      if (data.scheduled) {
        toast.success(`Blast dijadwalkan untuk ${new Date(schedule).toLocaleString("id-ID")}`);
        setStep(1); setSelected({}); setTemplateId(""); setUseSchedule(false); setSchedule("");
      } else {
        toast.success("Blast dimulai!");
        setStep(4);
        setJobId(data.job_id);
        setJob({ status: "queued", sent: 0, failed: 0, skipped: 0, total_leads: data.total, progress_pct: 0 });
        connectWs(data.job_id);
      }
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  const cancel = async () => {
    if (!jobId) return;
    try { await api.post(`/blast/wa/${jobId}/cancel`); toast.success("Membatalkan blast..."); } catch {}
  };

  const resume = async () => {
    if (!jobId) return;
    try {
      await api.post(`/wa/jobs/${jobId}/resume`);
      toast.success("Blast dilanjutkan");
      connectWs(jobId);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const canBlast = (quota?.blast_wa?.limit ?? 0) !== 0;
  const deviceOnline = waConnected && waDevice?.connected !== false;
  const Steps = [
    { label: "Pilih Lead", icon: Users },
    { label: "Template", icon: Eye },
    { label: "Pengaturan", icon: Settings2 },
    { label: "Progress", icon: Send },
  ];

  // Render template preview with first selected lead variables substituted.
  // Must match backend `render_template` in r_blast.py exactly.
  const renderPreview = (tpl, lead) => {
    if (!tpl) return "";
    let content = tpl.content || "";
    if (!lead) return content;
    const vars = {
      "{nama_bisnis}": lead.business_name || "",
      "{kota}": lead.city || "",
      "{kategori}": lead.category || "",
      "{website}": lead.website || "",
      "{rating}": lead.rating != null ? String(lead.rating) : "",
      "{nama_user}": "kamu",
    };
    Object.entries(vars).forEach(([k, v]) => {
      content = content.split(k).join(v);
    });
    return content;
  };

  return (
    <div className="space-y-6 w-full">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="inline-flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-emerald-100 grid place-items-center shrink-0">
              <Send className="w-5 h-5 text-emerald-600" />
            </span>
            <h1 className="font-display text-3xl font-extrabold">WA Blast</h1>
          </span>
          <p className="text-slate-500 mt-1">Kirim pesan WhatsApp massal ke lead pilihan kamu.</p>
        </div>
        {waConnected && (
          <div className={`rounded-xl border px-4 py-2 text-sm ${deviceOnline ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`} data-testid="blast-wa-device">
            <span className={deviceOnline ? "text-emerald-800" : "text-rose-800"}>
              📱 <b>{waDevice.name || "Akun Fonnte"}</b> · {waDevice.device || "—"} · {deviceOnline ? "Online" : "Offline"}
            </span>
            <span className={`ml-2 ${deviceOnline ? "text-emerald-600" : "text-rose-600"}`}>
              {waDevice.quota != null ? `sisa kuota ${Number(waDevice.quota).toLocaleString("id-ID")}` : ""}
            </span>
          </div>
        )}
      </div>

      <UpgradeGate active={!canBlast} feature="WA Blast">
        <>
          {/* Disconnect warning shown whenever device is offline */}
          <DeviceDisconnectWarning device={waDevice} onRefresh={loadWa} onReconnected={loadWa} />

          {/* Stepper */}
          <div className="flex items-center gap-2">
            {Steps.map((s, i) => (
              <div key={s.label} className="flex items-center gap-2 flex-1">
                <div className={`w-9 h-9 rounded-full grid place-items-center text-xs font-bold transition ${step > i + 1 ? "bg-emerald-500 text-white" : step === i + 1 ? "bg-sky-500 text-white ring-4 ring-sky-100" : "bg-slate-100 text-slate-400"}`}>
                  {step > i + 1 ? <Check className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
                </div>
                <span className={`text-sm hidden sm:block ${step === i + 1 ? "font-semibold text-slate-800" : "text-slate-400"}`}>{s.label}</span>
                {i < Steps.length - 1 && <div className="flex-1 h-px bg-slate-200" />}
              </div>
            ))}
          </div>

          {/* Step 1 — full width lead picker */}
          {step === 1 && (
            <div className="space-y-4">
              <LeadPicker leads={leads} setLeads={setLeads} selected={selected} setSelected={setSelected} channel="wa" accent="sky" />
              <div className="flex justify-between items-center sticky bottom-4 bg-white/95 backdrop-blur border border-slate-200 rounded-2xl px-4 py-3 shadow-lg">
                <div className="text-sm">
                  <b className="text-sky-600">{selectedIds.length}</b> lead dipilih
                </div>
                <Button data-testid="blast-next-1" disabled={selectedIds.length === 0} onClick={() => setStep(2)} size="lg">
                  Lanjut ke Template <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2 — template + live preview using full width 2-col */}
          {step === 2 && (
            <div className="grid lg:grid-cols-3 gap-4">
              <Card className="p-5 lg:col-span-2">
                <h3 className="font-display font-bold mb-4">Pilih Template Pesan</h3>
                {templates.length === 0 ? (
                  <p className="text-sm text-slate-400 py-8 text-center">Belum ada template WA. Buat di halaman Template.</p>
                ) : (
                  <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                    {templates.map((t) => (
                      <label key={t.id} className={`block p-4 rounded-xl border cursor-pointer transition ${templateId === t.id ? "border-sky-400 bg-sky-50 ring-2 ring-sky-100" : "border-slate-200 hover:border-sky-200"}`} data-testid="blast-template-option">
                        <div className="flex items-center gap-2">
                          <input type="radio" name="tpl" checked={templateId === t.id} onChange={() => setTemplateId(t.id)} className="accent-sky-500" />
                          <span className="font-semibold text-slate-800">{t.name}</span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1 whitespace-pre-wrap line-clamp-3">{t.content}</p>
                      </label>
                    ))}
                  </div>
                )}
              </Card>
              <Card className="p-5 sticky top-4 self-start">
                <h4 className="font-display font-bold text-sm text-slate-600 mb-3 flex items-center gap-2"><Eye className="w-4 h-4 text-sky-500" /> Pratinjau</h4>
                {selectedTemplate ? (
                  <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                    {renderPreview(selectedTemplate, selectedLeads[0])}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">Pilih template untuk melihat pratinjau berdasarkan lead pertama.</p>
                )}
                <div className="mt-3 text-xs text-slate-500">
                  Variabel tersedia: <code className="text-sky-600">{"{nama_bisnis}"}</code>, <code className="text-sky-600">{"{kota}"}</code>, <code className="text-sky-600">{"{kategori}"}</code>, <code className="text-sky-600">{"{website}"}</code>, <code className="text-sky-600">{"{rating}"}</code>
                </div>
              </Card>
              <div className="lg:col-span-3 flex justify-between">
                <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4" /> Kembali</Button>
                <Button data-testid="blast-next-2" disabled={!templateId} onClick={() => setStep(3)}>Lanjut <ArrowRight className="w-4 h-4" /></Button>
              </div>
            </div>
          )}

          {/* Step 3 — settings, also 2 col */}
          {step === 3 && (
            <div className="grid lg:grid-cols-3 gap-4">
              <Card className="p-6 space-y-5 lg:col-span-2">
                <h3 className="font-display font-bold">Pengaturan Pengiriman</h3>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Nomor Pengirim (Fonnte BYOK)</label>
                  {waConnected ? (
                    <div className={`mt-1.5 rounded-xl border px-4 py-3 flex items-center justify-between ${deviceOnline ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`}>
                      <div>
                        <p className={`text-sm font-semibold ${deviceOnline ? "text-emerald-800" : "text-rose-800"}`}>
                          {waDevice.name || "Akun Fonnte"} · {waDevice.device || "—"}
                        </p>
                        <p className={`text-xs ${deviceOnline ? "text-emerald-600" : "text-rose-600"}`}>
                          Status: {waDevice.status || (deviceOnline ? "Terhubung" : "Disconnect")}{waDevice.quota != null ? ` · sisa kuota ${Number(waDevice.quota).toLocaleString("id-ID")}` : ""}{waDevice.messages != null ? ` · terkirim ${Number(waDevice.messages).toLocaleString("id-ID")}` : ""}
                        </p>
                      </div>
                      <Check className={`w-5 h-5 ${deviceOnline ? "text-emerald-500" : "text-rose-400"}`} />
                    </div>
                  ) : (
                    <div className="mt-1.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                      Belum ada akun WhatsApp terhubung. Hubungkan akun Fonnte (BYOK) di halaman <b>WA Devices</b>.
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-slate-700">Delay min (detik)</label>
                    <input type="number" min="3" value={delay.min} onChange={(e) => setDelay({ ...delay, min: Number(e.target.value) })} className="w-full mt-1.5 rounded-xl border border-slate-200 px-4 py-2.5 text-sm" />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-slate-700">Delay max (detik)</label>
                    <input type="number" min="5" value={delay.max} onChange={(e) => setDelay({ ...delay, max: Number(e.target.value) })} className="w-full mt-1.5 rounded-xl border border-slate-200 px-4 py-2.5 text-sm" />
                  </div>
                </div>
                {(delay.min < SAFE_MIN_DELAY || delay.max < 30) && (
                  <div className="flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 p-3" data-testid="blast-delay-warning">
                    <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
                    <div className="text-xs text-rose-700">
                      <b>Delay terlalu cepat — risiko device dibanned tinggi.</b>
                      <span className="block text-rose-600 mt-0.5">
                        Disarankan minimal <b>{SAFE_MIN_DELAY}–60 detik</b> antar pesan. WhatsApp gampang flag akun
                        yang kirim &gt; 1 pesan / 15 detik. Naikkan delay atau atur ulang di <b>WA Devices → Safety</b>.
                      </span>
                    </div>
                  </div>
                )}
                <label className="flex items-start gap-2.5 cursor-pointer rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <input type="checkbox" checked={ignoreCooldown} onChange={(e) => setIgnoreCooldown(e.target.checked)} data-testid="blast-ignore-cooldown" className="mt-0.5" />
                  <span className="text-sm text-amber-800">
                    <b>Abaikan cooldown 7 hari</b> — tetap kirim ke semua nomor terpilih meski sudah pernah dikirim sebelumnya.
                    <span className="block text-xs text-amber-600 mt-0.5">Hati-hati: pengiriman berulang dapat meningkatkan risiko nomor diblokir WhatsApp.</span>
                  </span>
                </label>
                <div className="rounded-xl border border-slate-200 p-4">
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input type="checkbox" checked={useSchedule} onChange={(e) => setUseSchedule(e.target.checked)} data-testid="blast-schedule-toggle" />
                    <span className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Clock className="w-4 h-4 text-sky-500" /> Jadwalkan untuk nanti</span>
                  </label>
                  {useSchedule && (
                    <input type="datetime-local" value={schedule} min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                      onChange={(e) => setSchedule(e.target.value)} data-testid="blast-schedule-input"
                      className="w-full mt-3 rounded-xl border border-slate-200 px-4 py-2.5 text-sm" />
                  )}
                </div>
              </Card>
              <Card className="p-5 self-start space-y-3">
                <h4 className="font-display font-bold text-sm text-slate-600 mb-1 flex items-center gap-2"><Clock className="w-4 h-4 text-sky-500" /> Ringkasan</h4>
                <div className="rounded-xl bg-sky-50 p-4 text-sm text-sky-900 space-y-1">
                  <p><b>{selectedIds.length}</b> pesan akan dikirim</p>
                  <p>Delay <b>{delay.min}–{delay.max}</b> detik</p>
                  <p>Estimasi durasi ~ <b>{Math.ceil((selectedIds.length * ((delay.min + delay.max) / 2)) / 60)}</b> menit</p>
                  <p>Cooldown 7 hari: <b>{ignoreCooldown ? "diabaikan" : "aktif"}</b></p>
                </div>
                {selectedTemplate && (
                  <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-xs text-slate-700 whitespace-pre-wrap leading-relaxed line-clamp-6">
                    {renderPreview(selectedTemplate, selectedLeads[0])}
                  </div>
                )}
              </Card>
              <div className="lg:col-span-3 flex justify-between">
                <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="w-4 h-4" /> Kembali</Button>
                <Button data-testid="blast-start" loading={loading} disabled={!deviceOnline} onClick={startBlast} size="lg"
                  title={!deviceOnline ? "Perangkat WhatsApp Fonnte kamu sedang offline. Scan ulang QR dulu." : ""}>
                  {useSchedule ? <><Clock className="w-4 h-4" /> Jadwalkan Blast</> : <><Send className="w-4 h-4" /> Mulai Blast ({selectedIds.length})</>}
                </Button>
              </div>
            </div>
          )}

          {/* Step 4 — progress (full width) */}
          {step === 4 && job && (
            <Card className="p-6" data-testid="blast-progress">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold flex items-center gap-2">
                  {["done", "cancelled", "failed"].includes(job.status) ? <Check className="w-5 h-5 text-emerald-500" /> :
                   job.status === "paused" ? <Shield className="w-5 h-5 text-amber-500" /> :
                   <Loader2 className="w-5 h-5 text-sky-500 animate-spin" />}
                  Blast {job.status}
                </h3>
                <Badge color={job.status === "paused" ? "amber" : "sky"}>{job.progress_pct}%</Badge>
              </div>

              {job.status === "paused" && job.pause_reason && (
                <div className="mb-4 rounded-xl border-2 border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="blast-paused-banner">
                  <Shield className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-display font-bold text-amber-900">Blast otomatis di-pause oleh WA Safety</p>
                    <p className="text-sm text-amber-800 mt-1">{job.pause_reason}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <Button data-testid="blast-resume" size="sm" onClick={resume} className="bg-amber-500 hover:bg-amber-600 text-white border-0">
                        <Play className="w-3.5 h-3.5" /> Lanjutkan
                      </Button>
                      <span className="text-xs text-amber-600">atau ubah pengaturan di <b>WA Devices → Safety</b>.</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-500 ${job.status === "paused" ? "bg-amber-500" : "bg-sky-500"}`} style={{ width: `${job.progress_pct}%` }} />
              </div>
              <div className="grid grid-cols-4 gap-3 mt-5 text-center">
                {[["Total", job.total_leads, "slate"], ["Terkirim", job.sent, "green"], ["Gagal", job.failed, "red"], ["Skip", job.skipped, "amber"]].map(([l, v]) => (
                  <div key={l} className="bg-slate-50 rounded-xl py-3">
                    <p className="font-display text-2xl font-extrabold">{v}</p>
                    <p className="text-xs text-slate-500">{l}</p>
                  </div>
                ))}
              </div>
              <BlastActivityFeed jobId={jobId} leads={leads} running={!["done", "cancelled", "failed", "paused"].includes(job.status)} channel="wa" accent="sky" />
              <div className="flex justify-between mt-5">
                {!["done", "cancelled", "failed", "paused"].includes(job.status) ?
                  <Button variant="danger" size="sm" onClick={cancel}><XCircle className="w-4 h-4" /> Batalkan</Button> : <div />}
                <Button variant="outline" onClick={() => { setStep(1); setJob(null); setSelected({}); setTemplateId(""); }}>Blast Baru</Button>
              </div>
            </Card>
          )}
        </>
      </UpgradeGate>
    </div>
  );
}
